import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  EquipmentSearch,
  ForemanDateToProcess,
  ForemanEquipmentDispatchImport,
  ForemanEquipmentImport,
  ForemanEquipmentListItem,
  ForemanJobImport,
  SaveEquipmentUsage,
} from '@ups/xplat/api/dto';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { formatDateAsIsoLocal } from '@ups/xplat/utils';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ForemanEquipmentImportService {
  httpFactory = inject(MyHttpClientFactory);
  http: HttpClient;

  constructor() {
    const url = environment.urls.spartaAPI;
    this.http = this.httpFactory.createHttpClient(
      url,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  getForemanJobList(includeCompleted: boolean): Observable<ForemanJobImport[]> {
    const url = `/api/foreman/equipment/import/jobs?includeCompleted=${includeCompleted}`;
    return this.http.get(url) as Observable<ForemanJobImport[]>;
  }

  getForemanEquipmentList(): Observable<ForemanEquipmentListItem[]> {
    const url = '/api/foreman/equipment/import/equipment';
    return this.http.get(url) as Observable<ForemanEquipmentListItem[]>;
  }

  getForemanDateList(
    foremanJobID: string,
    includeCompleted: boolean
  ): Observable<ForemanDateToProcess[]> {
    const url = `/api/foreman/equipment/import/${foremanJobID}/dates?includeCompleted=${includeCompleted}`;
    return this.http.get(url) as Observable<ForemanDateToProcess[]>;
  }

  getEquipmentRecordsToImport(
    foremanJobID: string,
    date: Date,
    includeCompleted: boolean,
    foremanCrew?: string,
    workOrder?: string,
    shift?: number
  ): Observable<ForemanEquipmentImport[]> {
    const url = `api/foreman/equipment/import/${foremanJobID}/${formatDateAsIsoLocal(
      date
    )}`;

    const queryParameters: string[] = [];

    if (includeCompleted) {
      queryParameters.push(`includeCompleted=${includeCompleted}`);
    }
    if (foremanCrew) {
      queryParameters.push(`foremanCrews=${foremanCrew}`);
    }
    if (workOrder) {
      queryParameters.push(`workOrders=${workOrder}`);
    }
    if (shift) {
      queryParameters.push(`shifts=${shift}`);
    }

    return this.http.get(url + '?' + queryParameters.join('&')) as Observable<
      ForemanEquipmentImport[]
    >;
  }

  importEquipmentRecords(
    foremanJobID: string,
    date: Date,
    dtos: SaveEquipmentUsage[]
  ): Observable<ForemanEquipmentImport[]> {
    const url = `api/foreman/equipment/import/${foremanJobID}/${formatDateAsIsoLocal(
      date
    )}`;

    return this.http.post(url, dtos) as Observable<ForemanEquipmentImport[]>;
  }

  getEquipmentDispatchData(
    jobID: string,
    template: string
  ): Observable<ForemanEquipmentDispatchImport[]> {
    let url = `/api/foreman/equipment/import/${jobID}/dispatch`;

    if (template) {
      url += `?template=${template}`;
    }

    return this.http.get(url) as Observable<ForemanEquipmentDispatchImport[]>;
  }

  getWeeklyAndMonthlyData(
    jobID: string,
    anyDateInTheWeek: Date
  ): Observable<EquipmentSearch[]> {
    const url = `/api/equipmentUsage/weeklyAndMonthly/${jobID}/${formatDateAsIsoLocal(
      anyDateInTheWeek
    )}`;
    return this.http.get(url) as Observable<EquipmentSearch[]>;
  }

  getEquipmentDispatchIDsWithRelatedUsages(
    jobID: string,
    date: Date
  ): Observable<string[]> {
    let url = `/api/equipmentUsage/equipment-dispatch-ids-with-related-usages/${jobID}`;
    const params: string[] = [];

    params.push(`date=${formatDateAsIsoLocal(date)}`);

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return this.http.get<string[]>(url);
  }
}
